/**
 * Query params that change which page a visitor actually sees, and therefore deserve a canonical
 * URL of their own.
 *
 * `type` qualifies: it switches the Mini PDP between the dog and cat products, picks the SKU type on
 * supplements, and routes cat referrals to a different product entirely. Dropping it would collapse
 * pages that rank independently.
 *
 * Everything else the storefront reads from the query string is presentational or attribution —
 * `color`/`size` merely preselect a swatch on a PDP whose content is otherwise identical, and
 * `ref`, `returnTo`, `utm_*`, `fbclid` and friends change nothing at all.
 */
const CONTENT_BEARING_PARAMS = new Set(['type']);

/**
 * Reduces a location's query string to the params worth indexing.
 *
 * SeoHelmet built its canonical (and hreflang) URLs from the raw query string, so every parameter
 * combination self-canonicalised. Search Console duly indexed `?color=pink&size=medium`,
 * `?ref=blog.tryfi.com` and `?returnTo=%2Fmanage` as separate pages, splitting signals across URLs
 * that render the same thing.
 *
 * Params are emitted in a stable order so a given page always resolves to exactly one canonical URL,
 * however the visitor's link happened to be ordered.
 */
export default function canonicalSearch(search: string): string {
  const kept = [...new URLSearchParams(search).entries()]
    .filter(([key]) => CONTENT_BEARING_PARAMS.has(key))
    .sort(([a], [b]) => a.localeCompare(b));

  return kept.length > 0 ? `?${new URLSearchParams(kept).toString()}` : '';
}
